import React from 'react'
import { useSelector } from 'react-redux'
import cn from 'classnames'
import Spinner from 'react-spinkit'
import { useHistory } from 'react-router-dom'
import { Form, Field } from 'react-final-form'
import styles from './index.module.scss'
import NormalLayoutContainer from '../shared/NormalLayoutContainer.js'
import PageTitle from '../../components/page-title/index.js'
import { TextareaInput } from '../../components/TextareaInput.js'
import { formatPhoneNumber } from '../../utilities/stringUtilities.js'
import QUERY_KEYS from '../../graphql/queryKeys.js'
import useQuery from '../../hooks/useQuery.js'
import ticketTypesQuery from '../../graphql/queries/ticketTypes.js'
import onlineScheduleCustomizationsQuery from '../../graphql/queries/onlineScheduleCustomizations.js'
import useMutation from '../../hooks/useMutation.js'
import updateOnlineScheduleCustomizationsMutations from '../../graphql/mutations/updateOnlineScheduleCustomizations.js'
import notify from '../../utilities/notify.js'
import { EmailValidator } from '../../utilities/commonValidators.js'
import { captureErrorAndNotify } from '../../utilities/errorHandlers.js'
import { ROUTES } from '../../routes.js'

export default function OnlineScheduleCompanyDetailsPage () {
  const history = useHistory()
  const { user, hauler } = useSelector(({ user, hauler }) => ({ user: user.user, hauler: user.hauler }))

  const { data: { ticketTypes }, isFetching: ticketTypesFetching } = useQuery(
    [QUERY_KEYS.ticketTypes, { haulerId: user.haulerId, status: 'ENABLED' }],
    ticketTypesQuery,
    {
      enabled: Boolean(user.haulerId),
      onError () {
        notify('error', 'Unable to retrieve ticket types list')
      },
      placeholderData: { ticketTypes: [] }
    })

  const { data: { onlineScheduleCustomizations: customizations }, isFetching: customizationsFetching } = useQuery(
    [QUERY_KEYS.onlineScheduleCustomizations, { haulerId: user.haulerId }],
    onlineScheduleCustomizationsQuery,
    {
      enabled: true,
      onError (err) {
        captureErrorAndNotify(err)
      },
      placeholderData: { defaults: {}, savedResults: {} }
    })

  const { mutateAsync: updateOnlineScheduleCustomizations, isLoading: isUpdating } = useMutation(updateOnlineScheduleCustomizationsMutations, {
    async onSuccess () {
      notify('success', 'Settings have been updated')
      history.push(ROUTES.onlineSchedule)
    },
    onError (error) {
      captureErrorAndNotify(error, 'There was an error saving the settings')
    }
  })

  function setDeliveryDefaults (form) {
    form.batch(() => {
      form.change('companyName', customizations.defaults?.companyName)
      form.change('companyPhone', customizations.defaults?.companyPhone ? formatPhoneNumber(customizations.defaults.companyPhone.slice(-10)) : '')
      form.change('sendAsName', customizations.defaults?.sendAsName)
      form.change('deliveryEmailSubject', customizations.defaults?.deliveryEmailSubject)
    })
  }

  function onSubmit (values) {
    updateOnlineScheduleCustomizations({
      haulerId: user.haulerId,
      settings: {
        companyName: values.companyName,
        companyPhone: values.companyPhone.replace(/\D/g, ''),
        sendAsName: values.sendAsName,
        deliveryEmailSubject: values.deliveryEmailSubject,
        contactEmail: values.contactEmail ?? '',
        deliveryTermsAndConditions: values.deliveryTermsAndConditions,
        deliveryTicketType: Number(values.deliveryTicketType)
      }
    })
  }

  function validate (values) {
    const errors = {}

    if (!values.companyName) {
      errors.companyName = 'Required'
    }
    // at some point, the following might not be optional because a hauler might have pickups and some other services running through here. IDK
    if (!values.deliveryTermsAndConditions) {
      errors.deliveryTermsAndConditions = 'Required'
    }
    if (!values.deliveryEmailSubject) {
      errors.deliveryEmailSubject = 'Required'
    }
    if (values.contactEmail != null && !EmailValidator.test(values.contactEmail)) {
      errors.contactEmail = 'Must be a valid email address'
    }
    if (!values.deliveryTicketType) {
      errors.deliveryTicketType = 'Required'
    }

    return errors
  }

  if (customizationsFetching || ticketTypesFetching) {
    return (
      <Spinner name='circle' fadeIn='none' />
    )
  }

  const initialValues = {
    ...customizations.savedResults,
    ...{
      companyPhone: formatPhoneNumber(customizations.savedResults?.companyPhone),
      deliveryTicketType: customizations.savedResults?.deliveryTicketType?.id
    }
  }
  const enableCardCapture = Boolean(customizations.savedResults?.enableCardCapture)
  return (
    <NormalLayoutContainer showBackLink>
      <PageTitle>
        Online Schedule: Customize Company Information (Step: 2/3)
      </PageTitle>
      <Form
        initialValues={initialValues}
        onSubmit={onSubmit}
        className={cn(styles.agreementsForm)}
        validate={values => validate(values)}>
        {({ form, handleSubmit, invalid, pristine }) => {
          return (
            <form autoComplete='off' onSubmit={handleSubmit}>
              { customizations.savedResults == null && (
                <div className={cn('dis-panel')}>
                  <div className='dis-panel-header'>
                    <h5>First Time?</h5>
                  </div>
                  <div className={cn('dis-panel-body')}>
                    <p>
                      The following fields are designed to provide basic company details to customers as they visit your Online Schedule site.
                      Click the button below to autofill default values to quickly get started.
                    </p>
                    <p>

                      Note: Edits and adjustments to this information can be made at any time on this form.
                    </p>
                    <div className={cn(styles.buttonWrapper)}>
                      <button
                        type='button'
                        onClick={() => setDeliveryDefaults(form)}
                        className={cn('dis-btn dis-btn-primary dis-btn-lg')}>
                        <span>Fill in Default Values</span>
                        <i className='material-icons'>start</i>
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <div className={cn('dis-panel')}>
                <div className='dis-panel-header'>
                  <h5>Company Information</h5>
                </div>
                <div className={cn('dis-panel-body')}>
                  {/** set up a button to fill out the company details only if nothing has been saved */}
                  <Field name='companyName'>
                    {({ input, meta }) => (
                      <div className={cn('form-group', styles.textInput, {
                        'has-error': meta.error && meta.touched
                      })}>
                        <label className='control-label' htmlFor='length'>Company Name</label>
                        <input
                          {...input}
                          type='text'
                          className='form-control'
                        />
                        {meta.error && meta.touched && <span>{meta.error}</span>}
                      </div>
                    )}
                  </Field>
                  <Field name='companyPhone'>
                    {({ input, meta }) => (
                      <div className={cn('form-group', styles.textInput, {
                        'has-error': meta.error && meta.touched
                      })}>
                        <label className='control-label' htmlFor='length'>Customer Support Phone Number (optional)</label>
                        <input
                          {...input}
                          type='text'
                          className='form-control'
                        />
                        {meta.error && meta.touched && <span>{meta.error}</span>}
                      </div>
                    )}
                  </Field>
                  <Field name='contactEmail'>
                    {({ input, meta }) => (
                      <div className={cn('form-group', styles.textInput, {
                        'has-error': meta.error && meta.touched
                      })}>
                        <label className='control-label' htmlFor='length'>Customer Support Email (optional)</label>
                        <input
                          {...input}
                          type='text'
                          className='form-control'
                        />
                        {meta.error && meta.touched && <span>{meta.error}</span>}
                      </div>
                    )}
                  </Field>
                  <Field name='deliveryEmailSubject'>
                    {({ input, meta }) => (
                      <div className={cn('form-group', styles.textInput, {
                        'has-error': meta.error && meta.touched
                      })}>
                        <label className='control-label' htmlFor='length'>Delivery Confirmation Email Subject</label>
                        <input
                          {...input}
                          type='text'
                          className='form-control'
                        />
                        {meta.error && meta.touched && <span>{meta.error}</span>}
                      </div>
                    )}
                  </Field>
                  <Field name='sendAsName'>
                    {({ input, meta }) => (
                      <div className={cn('form-group', styles.textInput, {
                        'has-error': meta.error && meta.touched
                      })}>
                        <label className='control-label' htmlFor='length'>Email Send as Name (optional)</label>
                        <input
                          {...input}
                          type='text'
                          className='form-control'
                        />
                        {meta.error && meta.touched && <span>{meta.error}</span>}
                      </div>
                    )}
                  </Field>
                  {hauler?.isWastepayEnabled && !hauler?.isWastepayApproved && !enableCardCapture && <NotWastepayApproved />}
                </div>
              </div>
              <div className={cn('dis-panel')}>
                <div className='dis-panel-header'>
                  <h5>Ticket Type</h5>
                </div>
                <div className={cn('dis-panel-body')}>
                  <Field name='deliveryTicketType'>
                    {({ input, meta }) => (<>
                      <div className={cn('form-group', {
                        'has-error': meta.error && meta.touched
                      })}>
                        <label htmlFor='deliveryTicketType'>
                          Select a Ticket Type for Online Schedule Tickets. This Ticket Type will be applied to any Ticket created through the online scheduler.
                        </label>
                        <select {...input} id='deliveryTicketType' className='form-control'>
                          <option value={null}></option>
                          {ticketTypes.map(tt => (
                            <option key={tt.id} value={tt.id}>{`${tt.name} (${tt.shortCode})`}</option>
                          ))}
                        </select>
                        {meta.error && meta.touched && <span>{meta.error}</span>}
                      </div>
                    </>)}
                  </Field>
                </div>
              </div>
              <div className={cn('dis-panel')}>
                <div className='dis-panel-header'>
                  <h5>Terms and Conditions Settings</h5>
                </div>
                <div className={cn('dis-panel-body')}>
                  <Field
                    label={'Enter an explanation of the terms and conditions for an Online Scheduled service.'}
                    component={TextareaInput}
                    name='deliveryTermsAndConditions'
                    placeholder=''
                    maxLength={'100000'}
                    className={cn(styles.wrapperTAndC)}
                    textAreaClassName={cn(styles.tAndC)}
                  />
                  <div className={cn(styles.buttonWrapper)}>
                    <button
                      type='submit'
                      className={cn('dis-btn dis-btn-primary dis-btn-lg')}
                      disabled={pristine || invalid || isUpdating}>
                      <span>Save Changes</span>
                      <i className='material-icons'>check</i>
                    </button>
                  </div>
                </div>
              </div>
            </form>
          )
        }}
      </Form>
    </NormalLayoutContainer>
  )
}
function NotWastepayApproved () {
  return (
    <div className={styles.wastepayPromoCard}>
      <h6>NEW!</h6>
      <h2 className={styles.wastepayPromoTitle}>Credit Card Processing</h2>
      <p>With the new WastePay integration, charge a credit card from a ticket!</p>
      <a href='https://www.dispatcher.com/payments-demo' target='_blank' rel='noopener nofollow noreferrer'>Learn More</a>
    </div>
  )
}
